/**
 * ShareButton Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Social sharing button for property pages. Allows users to share properties via social media.
 * 
 * Features:
 * - Share to social media (Facebook, Twitter, WhatsApp, Email)
 * - Copy link to clipboard
 * - Analytics tracking
 * - Responsive design
 * 
 * Dependencies:
 * - React 19.2.4 (JSX)
 * - Font Awesome 6.5 (icons)
 * - TailwindCSS 4.1 (styling)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation
 */

import React, { useState } from 'react';
import { ProIcon } from './ProIcon';
import { trackShare } from '../lib/analytics';
import { DESIGN_TOKENS } from '../designTokens';

interface ShareButtonProps {
  url?: string;
  title?: string;
  className?: string;
  iconOnly?: boolean;
}

export const ShareButton: React.FC<ShareButtonProps> = ({ 
  url, 
  title, 
  className = "",
  iconOnly = false
}) => {
  const [isShared, setIsShared] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleShare = async () => {
    const shareUrl = url || window.location.href;
    const shareTitle = title || document.title;

    // Track share attempt
    trackShare('share_attempt', 'property', shareUrl);

    // Check if Web Share API is available (modern mobile browsers)
    if (navigator.share && isSecureContext()) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
          text: `Check out this amazing rental in Marbella!`
        });
        setIsShared(true);
        trackShare('native_share', 'property', shareUrl);
        setTimeout(() => setIsShared(false), 2000);
        return;
      } catch (err) {
        // User cancelled or error occurred, fall back to clipboard
        if ((err as Error).name !== 'AbortError') {
          console.warn('Share failed:', err);
        }
      }
    }

    // Fallback: Copy to clipboard with multiple methods for maximum compatibility
    try {
      // Method 1: Modern Clipboard API (HTTPS required)
      if (navigator.clipboard && isSecureContext()) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Method 2: Legacy approach for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        // Try multiple execCommand commands
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (!successful) {
          throw new Error('Copy failed');
        }
      }

      setIsShared(true);
      trackShare('copy_link', 'property', shareUrl);
      setShowTooltip(true);
      
      // Hide tooltip after 2 seconds
      setTimeout(() => {
        setShowTooltip(false);
        setTimeout(() => setIsShared(false), 300);
      }, 2000);

    } catch (err) {
      // Final fallback: Show manual copy prompt
      console.warn('Clipboard copy failed:', err);
      const message = `Link: ${shareUrl}`;
      
      // Try to open a new window with the link
      try {
        const popup = window.open('', '_blank', 'width=400,height=200');
        if (popup) {
          popup.document.write(`
            <html>
              <body style="font-family: ${DESIGN_TOKENS.typography.fontSans}; padding: 20px; text-align: center;">
                <h2>Share this Rental</h2>
                <p>Copy this link:</p>
                <input type="text" value="${shareUrl}" readonly 
                  style="width: 100%; padding: 10px; margin: 10px 0; font-size: 16px;"
                  onclick="this.select()">
                <button onclick="window.close()" 
                  style="padding: 10px 20px; background: ${DESIGN_TOKENS.colors.brandOceanDark}; color: ${DESIGN_TOKENS.colors.white}; border: none; cursor: pointer;">
                  Close
                </button>
              </body>
            </html>
          `);
          popup.document.close();
        }
      } catch (popupErr) {
        // Last resort: alert
        alert(`Copy this link: ${shareUrl}`);
      }
      
      trackShare('fallback_share', 'property', shareUrl);
    }
  };

  // Check if context is secure (HTTPS or localhost)
  const isSecureContext = (): boolean => {
    return window.isSecureContext || 
           window.location.protocol === 'https:' || 
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  };

  return (
    <div className="relative">
      <button
        onClick={handleShare}
        className={iconOnly 
          ? `h-8 w-8 rounded-full bg-white/90 backdrop-blur shadow-md flex items-center justify-center ${className}`
          : `ui-btn-secondary p-4 normal-case tracking-normal shadow-sm group gap-2 ${className}`
        }
        aria-label="Share property"
        title="Share this property"
      >
        <ProIcon 
          icon={isShared ? 'link' : 'circle-share-nodes'} 
          family={isShared ? 'light' : 'solid'}
          className={`transition-all duration-3 ${
            iconOnly 
              ? 'text-xl text-brand-ocean'
              : isShared 
                ? 'text-white text-2xl' 
                : 'text-white group-hover:text-white/90 text-2xl'
          }`} 
        />
        {!iconOnly && (
          <span className={`ui-eyebrow transition-all duration-300 ${
            isShared 
              ? 'text-white' 
              : 'text-white group-hover:text-white/90'
          }`}>
            {isShared ? 'Copied!' : 'Share'}
          </span>
        )}
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div 
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-slate-900 text-white text-xs font-medium rounded-lg whitespace-nowrap ui-tooltip-fade-in"
          role="tooltip"
        >
          Link copied to clipboard!
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
            <div className="w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-slate-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};
